import { Box, Stack, TextField, Typography, Alert, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { IconChevronDown } from '@tabler/icons-react';
import { calculateEconomics, rubles } from 'src/utils/intentEconomics';

import type { Economics, Quote } from 'src/services/IntentService';

export default function EconomicsCard({
    value,
    onChange,
    quote,
}: {
    value: Economics;
    onChange: (v: Economics) => void;
    quote?: Quote | null;
}) {
    const result = calculateEconomics(
        value,
        quote?.leads,
        Number(quote?.lead_cost || 0),
        Number(quote?.processing_cost || 0)
    );
    const field = (key: keyof Economics, label: string, placeholder: string, max?: number) => (
        <TextField
            fullWidth
            label={label}
            placeholder={placeholder}
            type="number"
            value={value[key] ?? ''}
            onChange={(e) => onChange({ ...value, [key]: e.target.value === '' ? null : e.target.value })}
            inputProps={{ min: 0, max, step: key === 'conversion' ? 0.1 : 0.01, inputMode: 'decimal' }}
            InputLabelProps={{ shrink: true }}
        />
    );
    return (
        <Box className="intent-economics-card">
            <Typography
                variant="h5"
                component="h2"
            >
                Сначала — экономика одного лида
            </Typography>
            <Typography
                color="text.secondary"
                mt={1}
                mb={3}
            >
                По вашим данным. Без обещания продаж.
            </Typography>
            <Box className="intent-two-columns">
                {field('contribution', 'Остаётся с продажи до привлечения, ₽', 'Например, 9000', 1000000)}
                {field('conversion', 'Продаж из 100 похожих лидов', 'Например, 5', 100)}
            </Box>
            <Stack
                mt={3}
                mb={2}
                spacing={0.5}
            >
                <Typography
                    color="text.secondary"
                    variant="body2"
                >
                    Ожидаемая ценность лида до затрат на привлечение
                </Typography>
                <Typography
                    component="p"
                    className="intent-value"
                >
                    {result ? rubles(result.value) : 'Нужны ваши данные'}
                </Typography>
                {!result && (
                    <Typography
                        color="text.secondary"
                        variant="body2"
                    >
                        Если конверсия неизвестна, выбирайте тест без прогноза доходности.
                    </Typography>
                )}
            </Stack>
            {quote && result && (
                <Box className="intent-result-grid">
                    <div>
                        <span>Ожидаемые продажи</span>
                        <strong>{new Intl.NumberFormat('ru-RU').format(result.sales)}</strong>
                    </div>
                    <div>
                        <span>Стоимость клиента</span>
                        <strong>{rubles(result.costPerCustomer)}</strong>
                    </div>
                    <div>
                        <span>После учтённых затрат</span>
                        <strong>{rubles(result.result)}</strong>
                    </div>
                </Box>
            )}
            {result && result.result !== null && result.result < 0 && (
                <Alert
                    severity="warning"
                    sx={{ mt: 2 }}
                >
                    При этих вводных пакет не окупается. Проверьте конверсию и расходы перед покупкой.
                </Alert>
            )}
            <Accordion
                disableGutters
                elevation={0}
                sx={{ mt: 2, '&:before': { display: 'none' } }}
            >
                <AccordionSummary expandIcon={<IconChevronDown size={18} />}>
                    Допущения и другие расходы
                </AccordionSummary>
                <AccordionDetails>
                    {field('other', 'Другие расходы на выбранный пакет, ₽', 'Не учтены', 1000000)}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={2}
                    >
                        Доход с продажи — после переменных затрат, до привлечения. Конверсия входящих заявок может
                        отличаться от конверсии найденных запросов. Фиксированные расходы компании не учтены.
                    </Typography>
                    {quote && result && (
                        <Typography
                            variant="body2"
                            mt={2}
                        >
                            Предельная цена лида: {rubles(result.maxLeadPrice)}. Конверсия для окупаемости:{' '}
                            {result.breakEvenConversion === null
                                ? 'не вычисляется'
                                : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(
                                      result.breakEvenConversion
                                  ) + '%'}
                            .
                        </Typography>
                    )}
                    {quote && result && (
                        <Stack mt={2}>
                            {[1, 3, 5].map((c) => (
                                <Typography
                                    key={c}
                                    variant="body2"
                                >
                                    При {c}%:{' '}
                                    {rubles(
                                        calculateEconomics(
                                            { ...value, conversion: c },
                                            quote.leads,
                                            Number(quote.lead_cost),
                                            Number(quote.processing_cost)
                                        )?.result
                                    )}{' '}
                                    после учтённых затрат.
                                </Typography>
                            ))}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
